import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';

export type AppModule =
    | 'dashboard' | 'members' | 'buyers' | 'milk_collection' | 'milk_sale'
    | 'rate_list'  | 'payments' | 'receipts'        | 'products'
    | 'purchases'  | 'sales'    | 'routes'           | 'accounts'
    | 'reports'    | 'milk_bill'| 'buyer_bill'       | 'daybook'          | 'ledger'
    | 'dairy_users'| 'route_report' | 'trial_balance' | 'route_milk_receipt'
    | 'milk_dispatch' | 'production' | 'milk_transport_bill' | 'chilling_bill' | 'head_load' | 'profit_loss' | 'balance_sheet'
    | 'stock_inventory' | 'sms_management';

export interface ModulePermission {
    can_view:   boolean;
    can_create: boolean;
    can_edit:   boolean;
    can_delete: boolean;
}

const FULL_ACCESS: ModulePermission = {
    can_view: true, can_create: true, can_edit: true, can_delete: true
};

const NO_ACCESS: ModulePermission = {
    can_view: false, can_create: false, can_edit: false, can_delete: false
};

@Injectable({ providedIn: 'root' })
export class PermissionService {

    constructor(private authService: AuthService) {}

    /** True if logged-in user is platform super-admin */
    isSuperAdmin(): boolean {
        const user = this.authService.getCurrentUser();
        const roleName = user?.role?.role_name || user?.role;
        return roleName === 'super-admin' || !!user?.is_super_admin;
    }

    /** True if logged-in user is the dairy admin (owner) */
    isDairyAdmin(): boolean {
        const user = this.authService.getCurrentUser();
        // Staff users created by dairy admin are never the dairy admin
        if (user?.is_staff_user) return false;
        // Explicit type match (case-insensitive)
        const typeName = (user?.dairy_user_type || user?.user_type?.type_name || '').toLowerCase();
        if (typeName === 'admin') return true;
        // Role-name match — PHP login sets role.role_name for milkusers (e.g. 'Admin')
        const roleName = (user?.role?.role_name || user?.role || '').toString().toLowerCase();
        if (roleName === 'admin') return true;
        // PHP signal: dairy owners have dairy_user_type=null and permissions=null
        // (PHP sets these to null explicitly for admin/owner accounts)
        return !user?.dairy_user_type && user?.permissions === null;
    }

    /** True if user is super-admin OR dairy admin (i.e. has full unrestricted access) */
    hasFullAccess(): boolean {
        return this.isSuperAdmin() || this.isDairyAdmin();
    }

    /** Returns the full permission object for a specific module */
    getModulePermission(module: AppModule): ModulePermission {
        if (this.hasFullAccess()) return { ...FULL_ACCESS };

        const user  = this.authService.getCurrentUser();
        const perms = user?.permissions;

        if (!perms || !perms[module]) return { ...NO_ACCESS };

        return {
            can_view:   !!perms[module].can_view,
            can_create: !!perms[module].can_create,
            can_edit:   !!perms[module].can_edit,
            can_delete: !!perms[module].can_delete,
        };
    }

    /** Shorthand: can the user perform a specific action on a module? */
    isAllowed(module: AppModule, action: 'view' | 'create' | 'edit' | 'delete'): boolean {
        return this.getModulePermission(module)[`can_${action}`];
    }

    /** Returns the current user's dairy user_type (e.g. 'collector', 'manager') */
    getDairyUserType(): string {
        const user = this.authService.getCurrentUser();
        return user?.dairy_user_type || user?.user_type?.type_name || '';
    }

    /**
     * Returns the created_by filter object to pass in API payloads.
     * Admin/super-admin see all transactions (returns {}),
     * sub-users see only their own (returns { created_by: userId }).
     */
    getTransactionUserFilter(): { created_by?: number } {
        if (this.hasFullAccess()) return {};
        const userId = this.authService.getUserId();
        return userId ? { created_by: userId } : {};
    }
}
